import Textfield from '@atlaskit/textfield';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useFlaggingCallback } from '@databases/flags';
import { messages } from '@databases/intl/messages';
import { useTextDelta } from '@databases/react/useTextDelta';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { DatabaseView } from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/utils/database/changesets';
import { isUniqueViewName } from '@databases/views/utils/viewName';
import { noop } from 'lodash-es';
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

interface ViewNameContainerProps {
  isActive?: boolean;
  isDefault: boolean;
}

const StyledTextField = styled(Textfield)<{ isDefault: boolean }>`
  &&& {
    border: none;
    background-color: transparent !important;
    width: ${(props) => (props.isDefault ? '200' : '250')}px;
  }

  &&& input {
    padding: 2px 1px;
    height: 24px;
    line-height: 24px;
    cursor: text;
    color: ${(props) =>
      props.isActive ? token('color.text.selected', colors.B300) : colors.N400};
  }
`;

const getMaxWidth = (isViewActionsOpen: boolean, isDefault: boolean) => {
  if (isViewActionsOpen) {
    return isDefault ? '190px' : '250px';
  } else {
    return isDefault ? '260px' : '320px';
  }
};

export const ViewNameContainer = styled.div<ViewNameContainerProps>`
  min-height: 20px;
  padding: 2px 1px;
  border-radius: 2px;
  max-width: ${(props) =>
    getMaxWidth(props.isViewActionsOpen, props.isDefault)} !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export interface ViewCardNameEditProps {
  name: string;
  isEditingName?: boolean;
  existingViewNames?: string[];
  handleEditToggle: (view: DatabaseView) => void;
  contentId: string;
  view: DatabaseView;
  setDuplicateViewNameErrorId: (value: string) => void;
  validationProps: any;
  isActive?: boolean;
  isDefault: boolean;
  isViewActionsOpen: boolean;
}

const ViewCardNameEdit: FunctionComponent<ViewCardNameEditProps> = ({
  name,
  isEditingName,
  existingViewNames = [],
  handleEditToggle,
  contentId,
  view,
  setDuplicateViewNameErrorId,
  validationProps,
  isActive = false,
  isDefault,
  isViewActionsOpen,
}) => {
  const [textareaRef, setTextareaRef] = useState<HTMLTextAreaElement | null>(null);

  const dispatch = defaultStoreUtils.useDispatch();

  useTextDelta(
    textareaRef,
    useCallback(
      (delta, newValue) => {
        dispatch(
          addChangeToDraft(contentId, 'main', {
            type: DatabaseChangeType.UPDATE_SAVED_VIEW_NAME,
            viewId: view.id,
            newName: newValue,
            delta,
          })
        );
      },
      [contentId, dispatch, view.id]
    )
  );

  textareaRef?.focus();

  useEffect(() => {
    if (existingViewNames && !isUniqueViewName(view.name, existingViewNames)) {
      setDuplicateViewNameErrorId(view.id);
    } else {
      setDuplicateViewNameErrorId('');
    }
  }, [existingViewNames, setDuplicateViewNameErrorId, view.id, view.name]);

  const { formatMessage } = useIntl();

  const handleKeyDown = useFlaggingCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleEditToggle(view);
      }
    },
    [handleEditToggle, view]
  );

  return isEditingName ? (
    <div>
      <StyledTextField
        testId="view-name-text-field"
        autoFocus={true}
        ref={setTextareaRef}
        {...validationProps}
        value={name}
        onChange={noop}
        onKeyDown={handleKeyDown}
        placeholder={formatMessage(messages['toolbar-views.view-name-placeholder'])}
        isDefault={isDefault}
        isActive={isActive}
        onClick={(e: React.MouseEvent) => {
          // prevent selection of view when clicking to edit name
          e.stopPropagation();
        }}
      />
    </div>
  ) : (
    <ViewNameContainer
      data-testid="view-name-container"
      isDefault={isDefault}
      isActive={isActive}
      isViewActionsOpen={isViewActionsOpen}
    >
      {name}
    </ViewNameContainer>
  );
};

export default ViewCardNameEdit;
